import { h } from 'preact';
import Store from 'store/store';
import { Modal } from 'utils/modal';
import { joinClasses } from 'utils/utils';
// import slugify from 'utils/slugify';
import * as jsonPointer from 'utils/jsonPointer';
import { isFocusStudio } from 'utils/studioConfig';
import { getThundrData } from 'utils/thundr';

import SimpleField from 'components/editor/settings/simpleField';
import FieldWrapper from 'components/editor/settings/fieldWrapper';
import FieldWithToggle from 'components/editor/settings/fieldWithToggle';
import ToggleField from 'components/editor/settings/toggleField';
import Button from 'components/button/button';
import Schema from 'components/pageSettings/schema';

import s from 'components/pageSettings/pageSettings.sss';

const normalisePage = (page) => page === 'home-ent' ? 'homeEnt' : page;

export default class PageSettings extends Modal {
	s = s;

	get page() {
		return normalisePage(this.props.page);
	}

	get pageData() {
		return jsonPointer.get(Store.get().data, this.props.dataRoot);
	}

	get movieData() {
		return jsonPointer.get(Store.get().data, this.props.dataRoot.split('/content')[0]);
	}

	get isDefaultPage() {
		const pages = this.movieData?.content?.pages || {};
		// Find the page with default settings or fallback to the home page
		const defaultPage = Object.entries(pages).find(([, page]) => page?.settings?.default)?.[0] || 'home';
		return this.page === defaultPage;
	}

	get movieTitle() {
		const movie = this.movieData;
		return movie?.content?.title || movie?.base_movie?.content?.title || movie?.title || 'Movie';
	}

	get title() {
		switch (this.page) {
			case 'home':
				return 'Landing page settings';
			case 'synopsis':
				return 'Synopsis page settings';
			case 'gallery':
				return 'Gallery page settings';
			case 'videos':
				return 'Videos page settings';
			case 'showtimes':
				return 'Showtimes page settings';
			case 'homeEnt':
				return 'Home entertainment page settings';
			default:
				return 'Page settings';
		}
	}

	get tabs() {
		const isFocus = isFocusStudio();
		const isHome = this.page === 'home';
		return [
			this.props.type === 'page' && (!isFocus || !isHome) && { key: 'settings', label: 'Settings' },
			{ key: 'metadata', label: 'Metadata & SEO' },
			isHome && { key: 'schema', label: 'Schema' },
		].filter(Boolean);
	}

	constructor(props) {
		super(props);

		this.state = {
			mode: this.tabs[0]?.key || 'settings', // settings, metadata, or schema
			thundrData: {},
		};

		this.onStoreUpdate = this.onStoreUpdate.bind(this);
		this.cancel = this.cancel.bind(this);
		this.save = this.save.bind(this);
		this.selectMode = this.selectMode.bind(this);
		this.onSetDefault = this.onSetDefault.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();
		Store.on('update', this.onStoreUpdate);
		this.loadThundrData();
	}

	componentWillUnmount() {
		super.componentWillUnmount?.();
		Store.off('update', this.onStoreUpdate);
	}

	async loadThundrData() {
		const thundrData = await getThundrData(this.movieData);
		this.setState({ thundrData });
	}

	onStoreUpdate(current, prev) {
		if (jsonPointer.get(current?.data, this.props.dataRoot) !== jsonPointer.get(prev?.data, this.props.dataRoot)) {
			// Queue a rerender (setState over forceUpdate as it can be batched)
			this.setState();
		}
	}

	cancel() {
		this.base.close();
	}

	async save() {
		return;
	}

	selectMode(e) {
		const mode = e.target.value;
		if (mode !== this.state.mode) {
			this.setState({ mode });
		}
	}

	onSetDefault() {
		// Get root path for pages (e.g. movies/{id}/pages)
		let pagesRoot = this.props.dataRoot.split('/');
		pagesRoot.pop(); // Remove page key to get to pages root
		pagesRoot = pagesRoot.join('/');
		
		// Unset previous default
		const pages = jsonPointer.get(Store.get().data, pagesRoot);
		Object.keys(pages).forEach((pageKey) => {
			jsonPointer.setDeep(Store.get().data, `${pagesRoot}/${pageKey}/settings/default`, false);
		});
		
		// Set new default
		jsonPointer.setDeep(Store.get().data, `${pagesRoot}/${this.page}/settings/default`, true);
	}

	renderDefaultPage() {
		const isHome = this.page === 'home';
		const isActive = isHome || jsonPointer.get(Store.get().data, `${this.props.dataRoot}/active`);
		const isDefault = this.isDefaultPage;

		return (
			<FieldWrapper label="Default page" help="Sets this page as the default and redirects here when accessing the main page">
				<Button primary icon="home" type="button" disabled={isDefault || !isActive} class={s.setDefaultButton} onClick={this.onSetDefault} autofocus>
					Set as default
				</Button>
				{isDefault && <div>Currently set as home</div>}
				{!isActive && <div>This page is not currently active</div>}
			</FieldWrapper>
		);
	}

	renderActiveToggle() {
		const isShowtimes = this.page === 'showtimes';
		const isHomeEnt = this.page === 'homeEnt';
		if (!isShowtimes && !isHomeEnt) return;

		const { sectionRoot, dataRoot } = this.props;

		const showtimesIdExists = jsonPointer.has(Store.get().data, `${sectionRoot}/content/showtimesId`)
			|| jsonPointer.has(Store.get().data, `${sectionRoot}/base_movie/content/showtimesId`);
		const hasHomeEntDate = !!jsonPointer.get(Store.get().data, `${sectionRoot}/content/dates/homeEnt`);
		const isDefault = this.isDefaultPage;
		const disableToggle = (isHomeEnt && !hasHomeEntDate) || (isShowtimes && !showtimesIdExists) || isDefault;
		
		const tooltipMessage = !disableToggle
			? `Toggle to set whether ${isShowtimes ? 'showtimes' : 'home entertainment details'} for this movie are live on the site.`
			: isDefault 
				? 'This page is set as the default page, so it cannot be deactivated.'
				: isHomeEnt && !hasHomeEntDate
					? 'Home Entertainment page cannot be set live until a Home Entertainment date has been added.'
					: 'Showtimes page cannot be set live until a showtimes source has been added.';

		return (
			<FieldWrapper
				label={isShowtimes ? 'Showtimes' : 'Home Entertainment'}
				help={tooltipMessage}
			>
				<ToggleField
					class={s.toggle}
					path={`${dataRoot}/active`}
					onInput={this.onActiveToggle}
					disabled={disableToggle}
				>
					{isShowtimes ? 'Set showtimes live' : 'Set home entertainment live'}
				</ToggleField>
			</FieldWrapper>
		);
	}

	renderSettings() {
		const isFocus = isFocusStudio();
		return (
			<div class={s.settings}>
				{this.props.type === 'page' && !isFocus && this.renderDefaultPage()}
				{/* TEMP: Removing "Slug" field as it's not used currently */}
				{/* <SimpleField
					label="Slug"
					path={`${this.props.dataRoot}/settings/slug`}
					preText="https://www.universalpictures.com/movies/"
					// update placeholder to get movie title
					placeholder={slugify(jsonPointer.get(Store.get().data, `${this.props.dataRoot}/title`), { removeDiacritics: true }) || 'new-movie'}
					help={"This will appear in the browser's title bar and may appear as the clickable title in search results. Try to limit it to 55 characters."}
				/> */}
				{this.props.type === 'page' && this.renderActiveToggle()}
			</div>
		);
	}

	getStudioName() {
		const isFocus = isFocusStudio();
		return isFocus ? 'Focus Features' : 'Universal Pictures';
	}

	getReleaseDate() {
		const releaseDate = this.movieData?.content?.dates?.release;
		if (!releaseDate) return '';
		
		const dateSplit = releaseDate.split('-');
		const dateParsed = new Date(dateSplit.length > 1 ? dateSplit[0] : parseInt(dateSplit[0]) + 1, (dateSplit[1] - 1) || 0, dateSplit[2] || 0);
		const day = dateSplit[2] ? dateParsed.getDate() + ', ' : '';
		const month = dateSplit[1] ? dateParsed.toLocaleString('default', { month: 'long' }) + ' ' : '';
		const year = dateSplit[0] ? dateParsed.getFullYear() : '';
		return `${month}${day}${year}`;
	}

	getMetaTitlePlaceholder() {
		const boltPageId = this.page && `bolt${this.page.charAt(0).toUpperCase()}${this.page.slice(1)}`;
		const metaTitle = this.state.thundrData?.doc?.metaTags?.pageSpecificMetaTags?.[boltPageId]?.title;
		if (metaTitle) return metaTitle
			?.replaceAll('$MOVIE_TITLE', this.movieTitle)
			?.replaceAll('$RELEASE_DATE', this.getReleaseDate() || '');

		const studio = this.getStudioName();
		return `${this.movieTitle} | ${studio}`;
	}

	getMetaDescriptionPlaceholder() {
		const releaseDate = this.getReleaseDate(this.movieData);

		const boltPageId = this.page && `bolt${this.page.charAt(0).toUpperCase()}${this.page.slice(1)}`;
		const metaDescription = this.state.thundrData?.doc?.metaTags?.pageSpecificMetaTags?.[boltPageId]?.description;
		if (metaDescription) return metaDescription
			?.replaceAll('$MOVIE_TITLE', this.movieTitle)
			?.replaceAll('$RELEASE_DATE', releaseDate || '');

		const studio = this.getStudioName();

		let cta = 'Learn about the film';
		if (this.movieData?.content?.trailers && this.movieData?.content?.trailers.length > 0) {
			cta = 'Watch the trailer';
		}
		if (this.movieData?.content?.dates?.ticketing) {
			cta += ' and book tickets';
		}
		cta += ` on the official ${studio} website.`;

		const release = releaseDate ? ` In cinemas ${releaseDate}.` : '';

		return `${this.movieTitle}, from ${studio}. ${cta}${release}`;
	}

	renderMetadata() {
		const basePath = this.props.dataRoot;
		const isFocus = isFocusStudio();

		// const placeholderTitle = this.getMetaTitlePlaceholder();
		// const placeholderDescription = this.getMetaDescriptionPlaceholder();
		const { htmlTitle, metaDescription } = this.pageData?.metadata || {};
		
		return (
			<div class={s.settings}>
				<FieldWrapper label="Meta robots" help={'Hides this page from search engine results like Google and Bing.'}>
					<ToggleField class={s.toggle} path={`${basePath}/metadata/metaRobots`}>
						Hide page from search engines
					</ToggleField>
				</FieldWrapper>
				<SimpleField
					label="HTML title"
					path={`${basePath}/metadata/htmlTitle`}
					// placeholder={placeholderTitle}
					placeholder="(automated)"
					help={"This will appear in the browser's title bar and may appear as the clickable title in search results. Try to limit it to 55 characters."}
				/>
				<SimpleField
					label="Meta description"
					path={`${basePath}/metadata/metaDescription`}
					// placeholder={placeholderDescription}
					placeholder="(automated)"
					help={'This may show as the page description in search engines. Try to keep it under 160 characters long. If you leave this blank, Google will generate a description using text from the page.'}
					multiline
				/>
				<FieldWithToggle
					label="Open Graph title"
					help={'This will appear as the page\'s title when the page is shared.'}
					fieldPath={`${basePath}/metadata/openGraphTitle`}
					// placeholder={htmlTitle || placeholderTitle}
					placeholder={htmlTitle || '(automated)'}
					togglePath={`${basePath}/metadata/openGraphTitleIsHtmlTitle`}
					toggleLabel="Same as meta title"
					// eslint-disable-next-line react/jsx-boolean-value
					toggleDefault={true}
				/>
				<FieldWithToggle
					label="Open Graph description"
					help={'This will appear as the page\'s description when the page is shared.'}
					fieldPath={`${basePath}/metadata/openGraphDescription`}
					// placeholder={metaDescription || placeholderDescription}
					placeholder={metaDescription || '(automated)'}
					togglePath={`${basePath}/metadata/openGraphDescriptionIsMetaDescription`}
					toggleLabel="Same as meta description"
					// eslint-disable-next-line react/jsx-boolean-value
					toggleDefault={true}
				/>
				<FieldWithToggle
					label="Canonical tags"
					help={'If this page is a duplicate of another page, you can change this canonical tag to the preferred URL you wish to show up in search results. This canonical URL should exactly match the full URL of the page you want to show in search results.'}
					fieldPath={`${basePath}/metadata/canonicalTags`}
					togglePath={`${basePath}/metadata/canonicalTagsAreSiteDefault`}
					toggleLabel="Site default"
				/>
				{!isFocus && (
					<SimpleField
						label="Google site verification code"
						path={`${basePath}/metadata/googleSiteVerificationCode`}
					/>
				)}
				{!isFocus && (
					<SimpleField
						label="Bing site verification code"
						path={`${basePath}/metadata/bingSiteVerificationCode`}
					/>
				)}
			</div>
		);
	}

	renderSchema() {
		return (<Schema basePath={this.props.dataRoot + '/schema'} />);
	}

	renderContent() {
		return (
			<div class={joinClasses(this.c('content'), s.content)}>
				<div class={s.tabs}>
					{this.tabs.map(tab => (
						<button type="button" class={joinClasses(s.tab, this.state.mode === tab.key && s.selected)} value={tab.key} onClick={this.selectMode} key={tab.key}>{tab.label}</button>
					))}
				</div>
				{this.state.mode === 'settings' && this.renderSettings()}
				{this.state.mode === 'metadata' && this.renderMetadata()}
				{this.state.mode === 'schema' && this.renderSchema()}
			</div>
		);
	}

	renderActions() {
		return (
			<div class={this.c('actions')}>
				<Button primary type="submit" value="CANCEL" key="cancel">Done</Button>
			</div>
		);
	}
}